import React, { useRef } from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  ChartEvent,
  ActiveElement,
  ChartOptions,
  TooltipItem,
  ScriptableContext,
} from 'chart.js';
import { Line } from 'react-chartjs-2';
import { TimelineData, TimelineArticle } from '../types/timeline';
import { SupportedLanguage } from '../types/language';
import { format, parseISO } from 'date-fns';
import { useTranslation } from 'react-i18next';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
);

interface InteractiveTimelineProps {
  timelineData: TimelineData;
  onDateSelect: (date: string, articles: TimelineArticle[]) => void;
  selectedDate: string | null;
  selectedCategories?: Set<string>;
  language: SupportedLanguage;
}

export const InteractiveTimeline: React.FC<InteractiveTimelineProps> = ({
  timelineData,
  onDateSelect,
  selectedDate,
  selectedCategories,
  language,
}) => {
  const chartRef = useRef<ChartJS<'line'>>(null);
  const { t } = useTranslation();
  const legendLabel = t('public.timeline.legend', { defaultValue: 'Legend' });
  const lowRiskLabel = t('public.timeline.lowRisk', { defaultValue: 'Low Risk' });
  const mediumRiskLabel = t('public.timeline.mediumRisk', { defaultValue: 'Medium Risk' });
  const highRiskLabel = t('public.timeline.highRisk', { defaultValue: 'High Risk' });
  const anomalyLabel = t('public.timeline.anomalyDetected', { defaultValue: 'Anomaly Detected' });
  const riskScoreLabel = t('public.timeline.riskScore', { defaultValue: 'Risk Score' });
  const articlesLabel = t('public.timeline.articles', { defaultValue: 'Articles' });
  const changeLabel = t('public.timeline.change', { defaultValue: 'Change' });
  const spikeLabel = t('public.timeline.riskSpikeDetected', { defaultValue: 'Risk spike detected' });
  const unusualLabel = t('public.timeline.unusualActivity', { defaultValue: 'Unusual activity' });
  const footerTip = t('public.timeline.clickToSeeArticles', { defaultValue: 'Click to see articles' });
  const topArticleLabel = t('public.timeline.topArticle', { defaultValue: 'Top article' });
  const averageRiskText = t('public.timeline.averageRisk', { defaultValue: 'Average Risk' });
  const noRecentAnalysesText = t('public.timeline.noRecentAnalyses', { defaultValue: 'No recent analyses' });
  const peakRiskText = t('public.timeline.peakRisk', { defaultValue: 'Peak Risk' });
  const articlesAnalyzedText = t('public.timeline.articlesAnalyzed', { defaultValue: 'Articles in range (live)' });
  const noDataLabel = t('public.timeline.noData', { defaultValue: 'No data' });
  const archivedLabel = t('public.timeline.archivedScore', { defaultValue: 'Archived score' });
  const archivedHint = t('public.timeline.archivedHint', {
    defaultValue: 'Archived score (older scoring method, not directly comparable)',
  });
  const unusualDaysText = t('public.timeline.unusualDays', { defaultValue: 'Unusual Days' });

  const yAxis = {
    safe: 'Safe',
    low: 'Low',
    medium: 'Medium',
    high: 'High',
    critical: 'Critical',
    extreme: 'Extreme',
  };

  const parseTimelineDate = (dateValue: string): Date => {
    return dateValue.includes('T') ? new Date(dateValue) : parseISO(dateValue);
  };

  const chartData = {
    labels: timelineData.timeline.map(day => day.date),
    datasets: [
      {
        label: t('public.timeline.overallRiskScore'),
        data: timelineData.timeline.map(day => day.overallScore),
        spanGaps: false,
        segment: {
          borderDash: (ctx: any) =>
            timelineData.timeline[ctx.p0DataIndex]?.dataSource === 'archived' ||
            timelineData.timeline[ctx.p1DataIndex]?.dataSource === 'archived'
              ? [5, 4]
              : undefined,
        },
        borderColor: 'rgb(59, 130, 246)',
        backgroundColor: 'rgba(59, 130, 246, 0.1)',
        borderWidth: 2,
        pointRadius: (context: ScriptableContext<'line'>) => {
          const index = context.dataIndex;
          const day = timelineData.timeline[index];
          // Larger points for days with articles or anomalies
          if (day.dataSource === 'archived') return 4;
          if (day.isAnomaly || day.isSpike) return 8;
          if (day.articleCount > 0) return 5;
          return 3;
        },
        pointBackgroundColor: (context: ScriptableContext<'line'>) => {
          const index = context.dataIndex;
          const day = timelineData.timeline[index];
          // Archived (legacy-scale) days are hollow points
          if (day.dataSource === 'archived') return 'rgba(255, 255, 255, 1)';
          // Color coding for different types of days
          if (day.isSpike) return 'rgb(239, 68, 68)'; // Red for spikes
          if (day.isAnomaly) return 'rgb(251, 146, 60)'; // Orange for anomalies
          if ((day.overallScore ?? 0) >= 7) return 'rgb(220, 38, 38)'; // Dark red for high risk
          if ((day.overallScore ?? 0) >= 5) return 'rgb(251, 191, 36)'; // Yellow for medium risk
          return 'rgb(34, 197, 94)'; // Green for low risk
        },
        pointBorderColor: (context: ScriptableContext<'line'>) => {
          const index = context.dataIndex;
          const day = timelineData.timeline[index];
          if (day.date === selectedDate) return 'rgb(99, 102, 241)';
          return day.dataSource === 'archived' ? 'rgb(107, 114, 128)' : 'rgba(255, 255, 255, 0.8)';
        },
        pointBorderWidth: (context: ScriptableContext<'line'>) => {
          const index = context.dataIndex;
          const day = timelineData.timeline[index];
          return day.date === selectedDate ? 3 : 2;
        },
        tension: 0.3,
      },
    ],
  };

  const options: ChartOptions<'line'> = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: {
      mode: 'index',
      intersect: false,
    },
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        callbacks: {
          title: (items: TooltipItem<'line'>[]) => {
            const index = items[0].dataIndex;
            const day = timelineData.timeline[index];
            return format(parseTimelineDate(day.date), 'EEEE, MMMM d, yyyy');
          },
          label: (item: TooltipItem<'line'>) => {
            const index = item.dataIndex;
            const day = timelineData.timeline[index];
            if (day.dataSource === 'none' || day.overallScore == null) {
              return [noDataLabel];
            }
            const lines =
              day.dataSource === 'archived'
                ? [`${riskScoreLabel}: ${day.overallScore} (${archivedLabel})`]
                : [`${riskScoreLabel}: ${day.overallScore}`, `${articlesLabel}: ${day.articleCount}`];
            
            if (day.scoreChange !== undefined && day.scoreChange !== 0) {
              const change = day.scoreChange > 0 ? `+${day.scoreChange.toFixed(1)}` : day.scoreChange.toFixed(1);
              lines.push(`${changeLabel}: ${change}`);
            }
            
            if (day.isSpike) lines.push(`⚠️ ${spikeLabel}`);
            if (day.isAnomaly) lines.push(`📊 ${unusualLabel}`);
            
            if (day.highestRisk) {
              lines.push('');
              lines.push(`${topArticleLabel}: "${day.highestRisk.title.substring(0, 50)}..."`);
            }
            
            return lines;
          },
          footer: () => footerTip,
        },
        backgroundColor: 'rgba(0, 0, 0, 0.9)',
        titleColor: 'white',
        bodyColor: 'white',
        footerColor: 'rgba(255, 255, 255, 0.6)',
        padding: 12,
        cornerRadius: 8,
        displayColors: false,
      },
    },
    scales: {
      x: {
        type: 'category',
        ticks: {
          maxRotation: 45,
          minRotation: 45,
          callback: function(tickValue: string | number, index: number) {
            // Show fewer labels on mobile
            const day = timelineData.timeline[index];
            if (window.innerWidth < 768) {
              // Mobile: show weekly labels
              return index % 7 === 0 ? format(parseTimelineDate(day.date), 'MMM d') : '';
            }
            // Desktop: show every 3 days
            return index % 3 === 0 ? format(parseTimelineDate(day.date), 'MMM d') : '';
          },
        },
        grid: {
          display: false,
        },
      },
      y: {
        min: 0,
        max: 10,
        ticks: {
          stepSize: 1,
          callback: (tickValue: string | number) => {
            const value = typeof tickValue === 'number' ? tickValue : Number(tickValue);
            const labels: Record<number, string> = {
              0: yAxis.safe,
              2: yAxis.low,
              4: yAxis.medium,
              6: yAxis.high,
              8: yAxis.critical,
              10: yAxis.extreme,
            };
            return labels[value] || value;
          },
        },
        grid: {
          display: true,
        },
      },
    },
    onClick: (event: ChartEvent, elements: ActiveElement[]) => {
      if (elements.length > 0) {
        const index = elements[0].index;
        const day = timelineData.timeline[index];
        // Articles are already filtered server-side, no need to filter again
        onDateSelect(day.date, day.articles);
      }
    },
    onHover: (event: ChartEvent, elements: ActiveElement[]) => {
      if (chartRef.current) {
        chartRef.current.canvas.style.cursor = elements.length > 0 ? 'pointer' : 'default';
      }
      
      // Hover state management removed as it's unused
    },
  };

  // Annotation data removed as it's unused

  return (
    <div className="relative">
      {/* Mobile legend: place above the chart so it doesn't cover data or block taps */}
      <div className="md:hidden px-3 pb-1">
        <div className="bg-white rounded-md border border-gray-200 p-2">
          <div className="text-xs font-medium text-gray-700 mb-1">{legendLabel}</div>
          <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs">
            <div className="flex items-center gap-2"><div className="w-3 h-3 rounded-full bg-green-500" /><span>{lowRiskLabel}</span></div>
            <div className="flex items-center gap-2"><div className="w-3 h-3 rounded-full bg-yellow-500" /><span>{mediumRiskLabel}</span></div>
            <div className="flex items-center gap-2"><div className="w-3 h-3 rounded-full bg-red-500" /><span>{highRiskLabel}</span></div>
            <div className="flex items-center gap-2"><div className="w-3 h-3 rounded-full bg-orange-500 ring-2 ring-orange-300" /><span>{anomalyLabel}</span></div>
            <div className="flex items-center gap-2"><div className="w-3 h-3 rounded-full bg-white border-2 border-gray-500" /><span>{archivedHint}</span></div>
          </div>
        </div>
      </div>

      {/* Desktop legend: overlay in corner without blocking interactions */}
      <div className="hidden md:block absolute top-2 right-2 z-10 pointer-events-none">
        <div className="bg-white/90 backdrop-blur-sm rounded-lg shadow-sm p-3 pointer-events-auto">
          <h4 className="text-sm font-medium text-gray-700 mb-2">{legendLabel}</h4>
          <div className="space-y-1 text-xs">
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-green-500"></div>
              <span>{lowRiskLabel}</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-yellow-500"></div>
              <span>{mediumRiskLabel}</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-red-500"></div>
              <span>{highRiskLabel}</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-orange-500 ring-2 ring-orange-300"></div>
              <span>{anomalyLabel}</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-white border-2 border-gray-500"></div>
              <span>{archivedHint}</span>
            </div>
          </div>
        </div>
      </div>

      <div className="h-[320px] md:h-[420px] p-3">
        <Line ref={chartRef} data={chartData} options={options} />
      </div>
      
      {/* Timeline summary stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-3">
        <div className="bg-gray-50 rounded-md p-3 text-center">
          <div className="text-xl font-bold text-gray-900">
            {timelineData.summary.averageRisk != null ? timelineData.summary.averageRisk.toFixed(1) : '—'}
          </div>
          <div className="text-sm text-gray-600">{averageRiskText}</div>
          {timelineData.summary.averageRisk == null && (
            <div className="text-xs text-gray-500 mt-1">{noRecentAnalysesText}</div>
          )}
        </div>
        <div className="bg-red-50 rounded-md p-3 text-center">
          <div className="text-xl font-bold text-red-900">
            {timelineData.summary.peakRisk != null ? timelineData.summary.peakRisk.toFixed(1) : '—'}
          </div>
          <div className="text-sm text-red-700">{peakRiskText}</div>
        </div>
        <div className="bg-blue-50 rounded-md p-3 text-center">
          <div className="text-xl font-bold text-blue-900">
            {timelineData.summary.totalArticles}
          </div>
          <div className="text-sm text-blue-700">{articlesAnalyzedText}</div>
          {(timelineData.summary.archivedDays || timelineData.summary.noDataDays) ? (
            <div className="text-xs text-blue-600 mt-1">
              {t('public.timeline.coverage', {
                defaultValue: '{{live}} live · {{archived}} archived · {{none}} no-data days',
                live: timelineData.summary.liveDays ?? 0,
                archived: timelineData.summary.archivedDays ?? 0,
                none: timelineData.summary.noDataDays ?? 0,
              })}
            </div>
          ) : null}
        </div>
        <div className="bg-orange-50 rounded-md p-3 text-center">
          <div className="text-xl font-bold text-orange-900">
            {timelineData.summary.anomalyDays}
          </div>
          <div className="text-sm text-orange-700">{unusualDaysText}</div>
        </div>
      </div>
    </div>
  );
};
